import { button, el, select, slider, toggle } from './dom';

export interface MenuCallbacks {
  onStart: () => void;
  onResume: () => void;
  onRestart: () => void;
  onQuitToMenu: () => void;
  onToggleCamera: () => void;
  onToggleHeadTracking: () => void;
  onSettingsChanged: (patch: Partial<SettingsView>) => void;
  onCalibrateHead: () => void;
  /** Open the camera test screen; the game starts the webcam for it. */
  onCameraTestStart?: () => void;
  /** Leave the camera test screen; the game releases the webcam if it is off. */
  onCameraTestStop?: () => void;
  /** Start a named level from the level-select screen. */
  onSelectLevel?: (id: string) => void;
  /** Open the 2D map builder. */
  onOpenBuilder?: () => void;
  /** The levels offered by the level-select screen. */
  getLevels?: () => Array<{ id: string; name: string; difficulty?: string }>;
  getSettings: () => SettingsView;
  getHeadTrackingStatus: () => HeadTrackingStatusView;
}

/** The live state of the optional webcam layer, shown under its toggle. */
export interface HeadTrackingStatusView {
  enabled: boolean;
  text: string;
}

export interface SettingsView {
  lookSensitivity: number;
  touchLookSensitivity: number;
  invertY: boolean;
  invertLookX: boolean;
  invertHeadTracking: boolean;
  fov: number;
  cameraMode: 'third' | 'first';
  transitionSpeed: number;
  sidewaysTransitions: boolean;
  horizonLock: boolean;
  reducedMotion: boolean;
  headTrackingEnabled: boolean;
  headTrackingSensitivity: number;
  headTrackingBodyShift: boolean;
  dodgeSwivelRange: number;
  thirdPersonParallax: number;
  cameraSwivel: boolean;
  masterVolume: number;
  sfxVolume: number;
  ambienceVolume: number;
  musicVolume: number;
  muted: boolean;
  uiScale: number;
  touchControls: 'auto' | 'on' | 'off';
}

/**
 * The mission brief: what the kit does, and how the two head dodges work.
 *
 * It is the shop window for the game's premise — a magnetic dog on a hijacked
 * ship where shooting the walls is the way to lose — so it says what the
 * weapons cost the player, not only what they do.
 */
const BRIEF_KIT: Array<[string, string]> = [
  [
    'Rifle (left mouse)',
    'Fast and loud. A hit on the ribbed exterior hull opens the ship to space and ends the run.',
  ],
  ['Net gun (T)', 'A six-metre capture net. It leaves the drone intact and helpless.'],
  ['Grapple (right mouse)', 'A magnetic hook: reel to any metal surface, or to a drone.'],
  ['Claw (F)', 'One strike finishes whatever the net caught, and shoves anything else in reach.'],
  ['Sneak (Shift)', 'A third of the noise, and slower. The HUD flags it when a drone hears you.'],
];

const BRIEF_DODGES: Array<[string, string]> = [
  ['Bullet time', 'Every detected shot slows the world and turns the view to the attacker.'],
  [
    'Settle onto a wall (Q)',
    'A sideways step keeps your view level; tilt your head, or press Q, to swivel the picture onto the new wall.',
  ],
  [
    'Head lean (webcam, H)',
    'Lean sideways to sway out of the bolt path — the body comes home when you straighten up. Forward or back to duck along the wall.',
  ],
];

/**
 * All screens outside the game: main menu, pause, settings, and the end of
 * round summaries.
 */
export class Menu {
  readonly root = el('div', 'menu hidden');

  private readonly title = el('h1', 'menu-title', 'MAGNETIC HOUND');
  private readonly subtitle = el('p', 'menu-subtitle');
  private readonly body = el('div', 'menu-body');
  private readonly actions = el('div', 'menu-actions');
  private lastBest: { score: number; time: number } | null = null;
  private settingStatus: HTMLElement | null = null;
  private headTrackingToggle: HTMLInputElement | null = null;
  private cameraTestStatus: HTMLElement | null = null;
  private cameraTestViewer: HTMLElement | null = null;

  constructor(
    parent: HTMLElement,
    private readonly callbacks: MenuCallbacks,
  ) {
    this.root.append(this.title, this.subtitle, this.body, this.actions);
    parent.append(this.root);
  }

  setVisible(visible: boolean): void {
    this.root.classList.toggle('hidden', !visible);
    // The camera test lightens the backdrop so the live view behind the panel
    // is the point of the screen. Every other screen builds different content,
    // so asking what is on screen keeps the two in step by itself.
    this.root.classList.toggle(
      'camera-test',
      this.body.querySelector('.camera-test-panel') !== null,
    );
  }

  /**
   * Update the live head-tracking line while the settings panel is open.
   *
   * Also re-syncs the checkbox, because a failed camera attempt reverts the
   * setting and the panel must not keep claiming it is on.
   */
  setHeadTrackingStatus(enabled: boolean, text: string): void {
    if (this.headTrackingToggle) this.headTrackingToggle.checked = enabled;
    if (!this.settingStatus) return;
    this.settingStatus.textContent = text;
    this.settingStatus.classList.toggle('active', enabled);
  }

  showMain(best: { score: number; time: number } | null = this.lastBest): void {
    this.lastBest = best;
    this.title.textContent = 'MAGNETIC HOUND';
    this.subtitle.textContent =
      'A magnetic quadruped aboard a hijacked ship. Every surface is a floor.';

    const brief = el('div', 'menu-brief');
    brief.append(
      el(
        'p',
        'brief-line',
        'The Meridian is hijacked and adrift, crawling with intruder drones. You are the HOUND: a four-legged retrieval unit with magnetic paws. Deck plating, bulkheads, the ceiling: every metal surface is a floor.',
      ),
      el(
        'p',
        'brief-line',
        'A rifle round through the hull is the end of the mission, so the rifle stays cold. Keep out of their sight lines, get close, and put them down without a shot.',
      ),
      el(
        'p',
        'brief-line',
        'Walls are stepped onto sideways: face along the seam you want to cross and press A or D. Forward and back stay on the surface you are standing on.',
      ),
      el('h3', 'brief-heading', 'KIT'),
      briefList(BRIEF_KIT),
      el('h3', 'brief-heading', 'HEAD DODGES'),
      briefList(BRIEF_DODGES),
    );

    this.body.replaceChildren(
      best ? el('p', 'menu-score', `BEST ${best.score} · ${best.time.toFixed(1)}s`) : el('span'),
      brief,
    );
    const levels = this.callbacks.getLevels?.() ?? [];
    this.actions.replaceChildren(
      button('Start run', () => this.callbacks.onStart()),
      ...(levels.length > 1 ? [button('Select level', () => this.showLevelSelect(levels))] : []),
      button('Calibration', () => this.openCameraTest()),
      ...(this.callbacks.onOpenBuilder
        ? [button('Map Builder', () => this.callbacks.onOpenBuilder?.())]
        : []),
      button('Settings', () => this.showSettings(this.callbacks.getSettings(), 'main')),
      button('Controls', () => this.showControls()),
      button('Credits', () => this.showCredits()),
    );
    this.setVisible(true);
  }

  /** Offer the campaign's levels. */
  showLevelSelect(levels?: Array<{ id: string; name: string; difficulty?: string }>): void {
    const list = levels ?? this.callbacks.getLevels?.() ?? [];
    this.title.textContent = 'SELECT LEVEL';
    this.subtitle.textContent = 'Pick a deck. The ship keeps its doors open.';
    const body = el('div', 'controls');
    for (const level of list) {
      const row = el('div', 'control-row');
      const name = el('span', 'control-name', level.name);
      if (level.difficulty) {
        // The tier is what the drones will throw at the player, so it belongs
        // next to the name rather than buried in the deck.
        name.append(el('span', 'level-difficulty', level.difficulty.toUpperCase()));
      }
      row.append(
        name,
        button('Play', () => this.callbacks.onSelectLevel?.(level.id), 'setting-button'),
      );
      body.append(row);
    }
    this.body.replaceChildren(body);
    this.actions.replaceChildren(button('Back', () => this.showMain()));
    this.setVisible(true);
  }

  showPause(): void {
    this.title.textContent = 'PAUSED';
    this.subtitle.textContent = 'The chamber waits.';
    this.body.replaceChildren();
    this.actions.replaceChildren(
      button('Resume', () => this.callbacks.onResume()),
      button('Settings', () => this.showSettings(this.callbacks.getSettings(), 'pause')),
      button('Controls', () => this.showControls()),
      button('Restart run', () => this.callbacks.onRestart()),
      button('Quit to menu', () => this.callbacks.onQuitToMenu()),
    );
    this.setVisible(true);
  }

  showGameOver(score: number, dronesLeft: number): void {
    this.title.textContent = 'HULL BREACH';
    this.subtitle.textContent = `${dronesLeft} drone${dronesLeft === 1 ? '' : 's'} still active.`;
    this.body.replaceChildren(el('p', 'menu-score', `SCORE ${score}`));
    this.actions.replaceChildren(
      button('Try again', () => this.callbacks.onRestart()),
      button('Quit to menu', () => this.callbacks.onQuitToMenu()),
    );
    this.setVisible(true);
  }

  showVictory(
    score: number,
    timeSeconds: number,
    best: number | null = null,
    nextLevel: { id: string; name: string } | null = null,
  ): void {
    this.title.textContent = 'DECK CLEARED';
    this.subtitle.textContent = 'Every drone is down. The hull holds.';
    this.body.replaceChildren(
      el('p', 'menu-score', `SCORE ${score}`),
      el('p', 'menu-score', `TIME ${timeSeconds.toFixed(1)}s`),
      best !== null ? el('p', 'menu-score', `BEST ${best}`) : el('span'),
    );
    this.actions.replaceChildren(
      ...(nextLevel
        ? [button('Next level', () => this.callbacks.onSelectLevel?.(nextLevel.id))]
        : []),
      button('Run it again', () => this.callbacks.onRestart()),
      button('Quit to menu', () => this.callbacks.onQuitToMenu()),
    );
    this.setVisible(true);
  }

  /**
   * Open the camera test.
   *
   * The game starts the webcam for it (the click is the user gesture), places
   * the robot at the spawn and keeps the off-axis window live behind this
   * panel, so the setup can be checked against the third-person view.
   */
  openCameraTest(): void {
    this.callbacks.onCameraTestStart?.();
    this.showCameraTest(this.callbacks.getSettings());
  }

  /**
   * The camera test screen.
   *
   * It is deliberately thin: the verification is the live view behind it, and
   * the readout is the proof that head poses are arriving and moving the
   * viewer. Everything here is the same switch as in Settings.
   */
  showCameraTest(settings: SettingsView = this.callbacks.getSettings()): void {
    this.title.textContent = 'CAMERA TEST';
    this.subtitle.textContent =
      'Move your head: the chamber should shift like a window, while the aim stays where you point it. V switches the viewpoint.';

    const status = this.callbacks.getHeadTrackingStatus();
    const statusLine = el('p', 'camera-test-status camera-test-wide', status.text);
    statusLine.classList.toggle('active', status.enabled);
    const viewerLine = el(
      'p',
      'camera-test-viewer camera-test-wide',
      'VIEWER x +0.0 · y +0.0 · z +0.0 cm',
    );
    this.cameraTestStatus = statusLine;
    this.cameraTestViewer = viewerLine;

    const headToggle = toggle({
      label: 'Head tracking (webcam)',
      value: status.enabled,
      onChange: (value) => this.apply({ headTrackingEnabled: value }),
    });
    this.headTrackingToggle = headToggle.querySelector('input');

    const parallax = slider({
      label: 'Head tracking parallax',
      min: 0,
      max: 3,
      step: 0.05,
      value: settings.headTrackingSensitivity,
      onChange: (value) => this.apply({ headTrackingSensitivity: value }),
    });
    parallax.classList.add('camera-test-wide');

    const armSlide = slider({
      label: 'Third-person parallax',
      min: 0,
      max: 3,
      step: 0.05,
      value: settings.thirdPersonParallax,
      onChange: (value) => this.apply({ thirdPersonParallax: value }),
    });
    armSlide.classList.add('camera-test-wide');

    const panel = el('div', 'settings camera-test-panel');
    panel.append(
      el(
        'p',
        'setting-hint camera-test-wide',
        'Needs a webcam, camera permission and localhost/HTTPS. The tracker moves the viewpoint only; the mouse still aims.',
      ),
      statusLine,
      viewerLine,
      headToggle,
      toggle({
        label: 'Head tracking body dodge',
        value: settings.headTrackingBodyShift,
        onChange: (value) => this.apply({ headTrackingBodyShift: value }),
      }),
      parallax,
      armSlide,
      button('Recalibrate head pose', () => this.callbacks.onCalibrateHead(), 'setting-button'),
      button('Switch viewpoint', () => this.callbacks.onToggleCamera(), 'setting-button'),
    );

    this.body.replaceChildren(panel);
    this.actions.replaceChildren(
      button('Start run', () => this.callbacks.onStart()),
      button('Back', () => {
        this.callbacks.onCameraTestStop?.();
        this.showMain();
      }),
    );
    this.setVisible(true);
  }

  /**
   * Update the live camera test readout. Called every frame while the screen is
   * open, so it writes text and nothing else.
   */
  setCameraTestLive(status: HeadTrackingStatusView, viewer: string): void {
    if (this.cameraTestStatus) {
      this.cameraTestStatus.textContent = status.text;
      this.cameraTestStatus.classList.toggle('active', status.enabled);
    }
    if (this.cameraTestViewer) this.cameraTestViewer.textContent = viewer;
  }

  get visible(): boolean {
    return !this.root.classList.contains('hidden');
  }

  /**
   * Ghost mode: fade the menu's backdrop so the live scene, the webcam preview
   * and the calibration map show through while the controls stay in front.
   */
  setGhost(active: boolean): void {
    this.root.classList.toggle('ghost', active);
  }

  showSettings(settings: SettingsView, returnTo: 'main' | 'pause'): void {
    this.title.textContent = 'SETTINGS';
    this.subtitle.textContent = 'Changes apply immediately.';

    const status = this.callbacks.getHeadTrackingStatus();
    const headToggle = toggle({
      label: 'Head tracking (webcam)',
      value: status.enabled,
      onChange: (value) => this.apply({ headTrackingEnabled: value }),
    });
    const headStatus = el('p', 'setting-status', status.text);
    headStatus.classList.toggle('active', status.enabled);
    this.headTrackingToggle = headToggle.querySelector('input');
    this.settingStatus = headStatus;

    const headBlock = el('div', 'setting setting-head-tracking');
    headBlock.append(
      headToggle,
      el(
        'p',
        'setting-hint',
        'Needs a webcam, camera permission and localhost/HTTPS. Press H during a run. Shifts the viewpoint with your head; the mouse still aims.',
      ),
      headStatus,
    );

    const form = el('div', 'settings');
    form.append(
      slider({
        label: 'Look sensitivity',
        min: 0.2,
        max: 3,
        step: 0.05,
        value: settings.lookSensitivity,
        onChange: (value) => this.apply({ lookSensitivity: value }),
      }),
      slider({
        label: 'Touch look sensitivity',
        min: 0.5,
        max: 5,
        step: 0.1,
        value: settings.touchLookSensitivity,
        format: (value) => `${value.toFixed(1)}×`,
        onChange: (value) => this.apply({ touchLookSensitivity: value }),
      }),
      toggle({
        label: 'Invert Y axis',
        value: settings.invertY,
        onChange: (value) => this.apply({ invertY: value }),
      }),
      toggle({
        label: 'Invert look X',
        value: settings.invertLookX,
        onChange: (value) => this.apply({ invertLookX: value }),
      }),
      toggle({
        label: 'Invert head tracking (off-axis shift)',
        value: settings.invertHeadTracking,
        onChange: (value) => this.apply({ invertHeadTracking: value }),
      }),
      slider({
        label: 'Field of view',
        min: 60,
        max: 110,
        step: 1,
        value: settings.fov,
        format: (value) => `${value.toFixed(0)}°`,
        onChange: (value) => this.apply({ fov: value }),
      }),
      toggle({
        label: 'Start in first person',
        value: settings.cameraMode === 'first',
        onChange: (value) => this.apply({ cameraMode: value ? 'first' : 'third' }),
      }),
      slider({
        label: 'Transition speed',
        min: 0.4,
        max: 2.5,
        step: 0.1,
        value: settings.transitionSpeed,
        onChange: (value) => this.apply({ transitionSpeed: value }),
      }),
      toggle({
        label: 'Walk onto walls (hold any direction)',
        value: settings.sidewaysTransitions,
        onChange: (value) => this.apply({ sidewaysTransitions: value }),
      }),
      toggle({
        label: 'Horizon lock (reduce camera roll)',
        value: settings.horizonLock,
        onChange: (value) => this.apply({ horizonLock: value }),
      }),
      toggle({
        label: 'Camera swivel (tilt or Q to settle onto a wall)',
        value: settings.cameraSwivel,
        onChange: (value) => this.apply({ cameraSwivel: value }),
      }),
      toggle({
        label: 'Reduced motion (less shake and flash)',
        value: settings.reducedMotion,
        onChange: (value) => this.apply({ reducedMotion: value }),
      }),
      select<'auto' | 'on' | 'off'>({
        label: 'Touch controls',
        value: settings.touchControls,
        options: [
          { value: 'auto', label: 'Auto-detect' },
          { value: 'on', label: 'Always on' },
          { value: 'off', label: 'Always off' },
        ],
        onChange: (value) => this.apply({ touchControls: value }),
      }),
      headBlock,
      button('Recalibrate head pose', () => this.callbacks.onCalibrateHead(), 'setting-button'),
      slider({
        label: 'Head tracking parallax',
        min: 0,
        max: 3,
        step: 0.05,
        value: settings.headTrackingSensitivity,
        onChange: (value) => this.apply({ headTrackingSensitivity: value }),
      }),
      toggle({
        label: 'Head tracking body dodge',
        value: settings.headTrackingBodyShift,
        onChange: (value) => this.apply({ headTrackingBodyShift: value }),
      }),
      slider({
        label: 'Dodge swivel range (bullet time)',
        min: 0.5,
        max: 2,
        step: 0.05,
        value: settings.dodgeSwivelRange,
        format: (value) => `${value.toFixed(2)} m`,
        onChange: (value) => this.apply({ dodgeSwivelRange: value }),
      }),
      slider({
        label: 'Master volume',
        min: 0,
        max: 1,
        step: 0.05,
        value: settings.masterVolume,
        onChange: (value) => this.apply({ masterVolume: value }),
      }),
      slider({
        label: 'Effects volume',
        min: 0,
        max: 1,
        step: 0.05,
        value: settings.sfxVolume,
        onChange: (value) => this.apply({ sfxVolume: value }),
      }),
      slider({
        label: 'Ambience volume',
        min: 0,
        max: 1,
        step: 0.05,
        value: settings.ambienceVolume,
        onChange: (value) => this.apply({ ambienceVolume: value }),
      }),
      slider({
        label: 'Music volume',
        min: 0,
        max: 1,
        step: 0.05,
        value: settings.musicVolume,
        onChange: (value) => this.apply({ musicVolume: value }),
      }),
      toggle({
        label: 'Mute all audio',
        value: settings.muted,
        onChange: (value) => this.apply({ muted: value }),
      }),
      slider({
        label: 'UI scale (text size)',
        min: 0.85,
        max: 1.5,
        step: 0.05,
        value: settings.uiScale,
        format: (value) => `${Math.round(value * 100)}%`,
        onChange: (value) => this.apply({ uiScale: value }),
      }),
    );

    this.body.replaceChildren(form);
    this.actions.replaceChildren(
      button('Back', () => (returnTo === 'main' ? this.showMain() : this.showPause())),
    );
    this.setVisible(true);
  }

  showControls(): void {
    this.title.textContent = 'CONTROLS';
    this.subtitle.textContent = 'Magnetic boots on every surface.';
    const list = el('div', 'controls');
    const rows: Array<[string, string]> = [
      ['Move (step onto walls)', 'W A S D'],
      ['Look / aim', 'Mouse'],
      ['Rifle', 'Left mouse'],
      ['Magnetic grapple', 'Right mouse'],
      ['Capture net', 'T, then left mouse'],
      ['Claw strike', 'F'],
      ['Sneak (slower, quieter)', 'Shift'],
      ['Jump / launch', 'Space'],
      ['Crouch (charges a stronger launch)', 'Ctrl or C'],
      ['Grab a surface while flying', 'E'],
      ['Switch viewpoint', 'V'],
      ['Settle the view onto a wall', 'Q (or a head tilt)'],
      ['Head tracking on / off', 'H'],
      ['Recalibrate head pose', 'Calibrate button in settings'],
      ['Pause', 'Esc'],
      ['Restart run', 'R'],
      ['Debug overlay', 'F3'],
      ['Touch: move', 'Left thumb stick'],
      ['Touch: look / aim', 'Drag anywhere else'],
      ['Touch: fire, jump, claw, hook', 'Right-hand buttons'],
      ['Touch: net, sneak, crouch, grab', 'Right-hand buttons'],
      ['Touch: view, pause', 'Top-left buttons'],
    ];
    for (const [name, key] of rows) {
      const row = el('div', 'control-row');
      row.append(el('span', 'control-name', name), el('span', 'control-key', key));
      list.append(row);
    }
    this.body.replaceChildren(list);
    this.actions.replaceChildren(button('Back', () => this.showMain()));
    this.setVisible(true);
  }

  showCredits(): void {
    this.title.textContent = 'CREDITS';
    this.subtitle.textContent = 'Built with open tools.';
    const list = el('div', 'controls');
    const rows: Array<[string, string]> = [
      ['Design, code, art, audio', 'Magnetic Hound contributors'],
      ['Engine', 'three.js (MIT)'],
      ['Build and tests', 'Vite, Vitest, TypeScript, ESLint, Prettier (MIT / Apache-2.0)'],
      ['Head tracking', 'Google MediaPipe Tasks (Apache-2.0), self-hosted'],
      ['Cover props', 'Kenney Space Kit (CC0)'],
      ['Off-axis window', 'Inspired by MindDock/off-axis-demo (MIT)'],
      ['Audio', 'All sounds synthesised at runtime; no third-party samples'],
    ];
    for (const [name, credit] of rows) {
      const row = el('div', 'control-row');
      row.append(el('span', 'control-name', name), el('span', 'control-key', credit));
      list.append(row);
    }
    this.body.replaceChildren(list);
    this.actions.replaceChildren(button('Back', () => this.showMain()));
    this.setVisible(true);
  }

  /** Apply a partial settings change and notify the game. */
  apply(patch: Partial<SettingsView>): void {
    this.callbacks.onSettingsChanged(patch);
  }
}

/** A two-column list, the same shape the controls and credits screens use. */
function briefList(rows: Array<[string, string]>): HTMLElement {
  const list = el('div', 'controls');
  for (const [name, detail] of rows) {
    const row = el('div', 'control-row');
    row.append(el('span', 'control-name', name), el('span', 'control-key', detail));
    list.append(row);
  }
  return list;
}
